import React, { useEffect, useMemo, useState } from 'react';
import { RevenueReportAggregateEnum } from 'anchor-scout/types';
import { addMonths, endOfMonth, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Button, Checkbox, DropdownMenu, FilterPicker, FilterPickerGroup, KeyboardDatePicker, MenuItem, MultiSelectFilterPicker, Page, PageContentHeader, RadioButtonWithLabel, Spinner, TableList, TextButton, TextField, Typography } from 'seagull';
import { routes } from "../../../constants";
import { useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import styles from "./RevenueBreakdown.module.scss";
import classNames from 'classnames';
import { compact } from 'lodash';
import { formatMediumUSDate, formatShortUSDate } from "../../../utils";
import { exportCsv } from "../../../utils/csv.utils";
export let DataRange = /*#__PURE__*/function (DataRange) {
  DataRange["Last12Months"] = "last12Months";
  DataRange["CurrentMonth"] = "currentMonth";
  DataRange["CurrentQuarter"] = "currentQuarter";
  DataRange["CurrentYear"] = "currentYear";
  DataRange["AllTime"] = "allTime";
  DataRange["Custom"] = "Custom";
  return DataRange;
}({});
export const dataRangeNames = {
  [DataRange.Last12Months]: () => 'Last 12 months',
  [DataRange.CurrentMonth]: () => 'Current month',
  [DataRange.CurrentQuarter]: () => 'Current quarter',
  [DataRange.CurrentYear]: () => 'Current year',
  [DataRange.AllTime]: () => 'All time',
  [DataRange.Custom]: (start, end) => "Custom range (".concat(formatMediumUSDate(new Date(start)), " - ").concat(formatMediumUSDate(new Date(end)))
};
export const dataRangeOptions = [{
  value: DataRange.Last12Months,
  text: 'Last 12 months'
}, {
  value: DataRange.CurrentMonth,
  text: 'Current month'
}, {
  value: DataRange.CurrentQuarter,
  text: 'Current quarter'
}, {
  value: DataRange.CurrentYear,
  text: 'Current year'
}, {
  value: DataRange.AllTime,
  text: 'All time'
}, {
  value: DataRange.Custom,
  text: 'Custom'
}];
function calculateStartDate(startDate, dataRange) {
  switch (dataRange) {
    case DataRange.Last12Months:
      return formatShortUSDate(addMonths(startOfMonth(new Date()), -12));
    case DataRange.CurrentMonth:
      return formatShortUSDate(startOfMonth(new Date()));
    case DataRange.CurrentQuarter:
      return formatShortUSDate(startOfQuarter(new Date()));
    case DataRange.CurrentYear:
      return formatShortUSDate(startOfYear(new Date()));
    case DataRange.AllTime:
      return undefined;
    default:
      return startDate;
  }
}
function calculateEndDate(endDate, dataRange) {
  switch (dataRange) {
    case DataRange.Last12Months:
      return formatShortUSDate(addMonths(endOfMonth(new Date()), -1));
    case DataRange.CurrentMonth:
    case DataRange.CurrentQuarter:
    case DataRange.CurrentYear:
      return formatShortUSDate(endOfMonth(new Date()));
    case DataRange.AllTime:
      return undefined;
    default:
      return endDate;
  }
}
function DataRangePicker(props) {
  const [dataRangeOption, setDataRangeOption] = useState(props.dataRangeOption);
  const [startDate, setStartDate] = useState(props.startDate);
  const [endDate, setEndDate] = useState(props.endDate);
  useEffect(() => {
    const startDate = calculateStartDate(formatShortUSDate(new Date()), dataRangeOption);
    const endDate = calculateEndDate(formatShortUSDate(new Date()), dataRangeOption);
    setStartDate(startDate);
    setEndDate(endDate);
  }, [dataRangeOption]);
  return /*#__PURE__*/React.createElement(FilterPicker, {
    "data-testid": "revenue-breakdown-report-data-range-picker",
    label: dataRangeNames[dataRangeOption](startDate, endDate),
    title: "Data range",
    highlightTrigger: false,
    onApply: () => props.onApply(dataRangeOption, startDate, endDate),
    onClose: () => {}
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.row, styles.rangePicker)
  }, /*#__PURE__*/React.createElement(TextField, {
    className: styles.dataRangeDropdown,
    select: true,
    value: dataRangeOption,
    onChange: e => {
      const selectedDataRange = e.target.value;
      setDataRangeOption(selectedDataRange);
    },
    testid: "revenue-breakdown-report-data-range-dropdown"
  }, dataRangeOptions.map((_ref, idx) => {
    let {
      value,
      text
    } = _ref;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: value,
      value: value,
      "data-testid": "revenue-breakdown-report-data-range-option-".concat(idx)
    }, text);
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("div", null, [DataRange.AllTime, DataRange.Custom].includes(dataRangeOption) ? null : /*#__PURE__*/React.createElement(Typography, {
    "data-testid": "revenue-breakdown-report-data-range-helper-text",
    variant: "paragraph1",
    noWrap: true
  }, startDate, " - ", endDate)))), dataRangeOption === DataRange.Custom ? /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement(KeyboardDatePicker, {
    "data-testid": "revenue-breakdown-report-data-range-start-date-picker",
    className: styles.customDatePicker,
    value: startDate,
    onChange: date => date != null && setStartDate(formatShortUSDate(date))
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph1",
    noWrap: true
  }, "\xA0-\xA0"), /*#__PURE__*/React.createElement(KeyboardDatePicker, {
    "data-testid": "revenue-breakdown-report-data-range-end-date-picker",
    className: styles.customDatePicker,
    value: endDate,
    onChange: date => date != null && setEndDate(formatShortUSDate(date))
  })) : null);
}
export const aggregateOptions = [{
  value: RevenueReportAggregateEnum.Day,
  text: 'Day'
}, {
  value: RevenueReportAggregateEnum.Week,
  text: 'Week'
}, {
  value: RevenueReportAggregateEnum.Month,
  text: 'Month'
}, {
  value: RevenueReportAggregateEnum.Quarter,
  text: 'Quarter'
}, {
  value: RevenueReportAggregateEnum.Year,
  text: 'Year'
}, {
  value: RevenueReportAggregateEnum.AllTime,
  text: 'All time'
}];
export const aggregateNames = {
  [RevenueReportAggregateEnum.Day]: 'Day',
  [RevenueReportAggregateEnum.Week]: 'Week',
  [RevenueReportAggregateEnum.Month]: 'Month',
  [RevenueReportAggregateEnum.Quarter]: 'Quarter',
  [RevenueReportAggregateEnum.Year]: 'Year',
  [RevenueReportAggregateEnum.AllTime]: 'All time'
};
function AggregatePicker(props) {
  const [aggregate, setAggregate] = useState(props.aggregate);
  return /*#__PURE__*/React.createElement(FilterPicker, {
    "data-testid": "revenue-breakdown-report-aggregate-picker",
    label: aggregate != null ? aggregateNames[aggregate] : 'None',
    title: "Time interval",
    highlightTrigger: false,
    onApply: () => props.onApply(aggregate),
    onClose: () => {}
  }, aggregateOptions.map(_ref2 => {
    let {
      value,
      text
    } = _ref2;
    return /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
      key: value,
      label: text,
      testId: "revenue-breakdown-report-aggregate-option-".concat(value),
      checked: aggregate === value,
      onChange: () => setAggregate(value)
    });
  }));
}
export let Breakdown = /*#__PURE__*/function (Breakdown) {
  Breakdown["Service"] = "service";
  Breakdown["Customer"] = "customer";
  return Breakdown;
}({});
export const breakdownOptions = [{
  value: Breakdown.Service,
  text: 'Service name'
}, {
  value: Breakdown.Customer,
  text: 'Company name'
}];
export const breakdownNames = {
  [Breakdown.Service]: 'Service',
  [Breakdown.Customer]: 'Client'
};
function BreakdownPicker(props) {
  const [breakdowns, setBreakdowns] = useState(props.breakdowns);
  return /*#__PURE__*/React.createElement(FilterPicker, {
    "data-testid": "revenue-breakdown-report-breakdowns-selector",
    label: breakdowns.length > 0 ? breakdowns.map(b => breakdownNames[b]).join(', ') : 'Breakdowns',
    title: "Breakdown by",
    highlightTrigger: false,
    onApply: () => props.onApply(breakdowns),
    onClose: () => {}
  }, /*#__PURE__*/React.createElement(Checkbox, {
    key: "all",
    name: "Select all",
    "data-testid": "revenue-breakdown-report-breakdown-option-all",
    checked: breakdownOptions.every(_ref3 => {
      let {
        value
      } = _ref3;
      return breakdowns.includes(value);
    }),
    onChange: () => {
      if (breakdowns.length === breakdownOptions.length) {
        setBreakdowns([]);
      } else {
        setBreakdowns([...breakdownOptions.map(_ref4 => {
          let {
            value
          } = _ref4;
          return value;
        })]);
      }
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph1"
  }, "Select all")), breakdownOptions.map(_ref5 => {
    let {
      value,
      text
    } = _ref5;
    return /*#__PURE__*/React.createElement(Checkbox, {
      key: value,
      name: text,
      "data-testid": "revenue-breakdown-report-breakdown-option-".concat(value),
      checked: breakdowns.includes(value),
      onChange: () => {
        if (breakdowns.includes(value)) {
          setBreakdowns(breakdowns.filter(b => b !== value));
        } else {
          setBreakdowns([...breakdowns, value]);
        }
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph1"
    }, text));
  }));
}
const revenueReportHeaderColumnDefs = {
  ['']: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    value: aggregateNames[aggregate]
  }, {
    value: 'Revenue'
  }]),
  [Breakdown.Service]: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    value: aggregateNames[aggregate]
  }, {
    value: 'Service name'
  }, {
    value: 'Revenue'
  }]),
  [Breakdown.Customer]: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    value: aggregateNames[aggregate]
  }, {
    value: 'Company name'
  }, {
    value: 'Revenue'
  }]),
  [[Breakdown.Customer, Breakdown.Service].join(',')]: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    value: aggregateNames[aggregate]
  }, {
    value: 'Company name'
  }, {
    value: 'Service name'
  }, {
    value: 'Revenue'
  }])
};
const revenueReportColumnDefs = {
  ['']: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    width: 476,
    sortable: false
  }, {
    width: 476,
    sortable: false
  }]),
  [Breakdown.Service]: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    width: 317,
    sortable: false
  }, {
    width: 317,
    sortable: false
  }, {
    width: 318,
    sortable: false
  }]),
  [Breakdown.Customer]: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    width: 317,
    sortable: false
  }, {
    width: 317,
    sortable: false
  }, {
    width: 318,
    sortable: false
  }]),
  [[Breakdown.Customer, Breakdown.Service].join(',')]: aggregate => compact([aggregate !== RevenueReportAggregateEnum.AllTime && {
    width: 238,
    sortable: false
  }, {
    width: 238,
    sortable: false
  }, {
    width: 238,
    sortable: false
  }, {
    width: 238,
    sortable: false
  }])
};
var FilterType = /*#__PURE__*/function (FilterType) {
  FilterType["CustomerName"] = "company";
  FilterType["ServiceName"] = "service";
  return FilterType;
}(FilterType || {});
const filterLabels = {
  [FilterType.CustomerName]: 'Company name',
  [FilterType.ServiceName]: 'Service name'
};
export function FilterSelector(props) {
  const {
    selected,
    onSelect
  } = props;
  const items = useMemo(() => {
    const getEntry = (type, text) => ({
      type,
      text,
      disabled: selected.includes(type)
    });
    const items = Object.values(FilterType).map(key => {
      //const type = FilterType[key as keyof typeof FilterType];
      return getEntry(key, filterLabels[key]);
    });
    return items;
  }, [selected]);
  function getMenuItem(type, text, disabled) {
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      key: type,
      skin: "primary",
      "data-testid": "revenue-breakdown-report-filter-picker-".concat(type),
      disabled: disabled,
      onClick: () => onSelect(type)
    }, text);
  }
  return /*#__PURE__*/React.createElement(DropdownMenu, {
    "data-testid": "revenue-breakdown-report-filter-picker",
    hAlign: "center",
    target: /*#__PURE__*/React.createElement(TextButton, {
      "data-testid": "revenue-breakdown-report-add-filter",
      underline: "hover"
    }, "+Add filter")
  }, items.map(_ref6 => {
    let {
      type,
      text,
      disabled
    } = _ref6;
    return getMenuItem(type, text, disabled);
  }));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type, component) {
  return {
    component,
    hasValue(v) {
      return v.length > 0;
    },
    getEmptyValue() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [];
    },
    serialize(data) {
      return {
        [type]: data
      };
    }
  };
}
function StringListFilter(label, filterOptions) {
  return function CustomerNameFilterComponent(props) {
    return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
      withSearchbox: true,
      removable: true,
      startOpen: props.startOpen,
      "data-testid": "revenue-breakdown-report-".concat(props.type, "-filter"),
      value: props.value,
      options: filterOptions.map(option => ({
        value: option,
        label: option
      })),
      label: label,
      onChange: props.onChange,
      onRemove: props.onRemove
    });
  };
}
export const makeFiltersConfig = (serviceNames, customerNames) => ({
  [FilterType.CustomerName]: getSimpleMultiSelectFilterConfig(FilterType.CustomerName, StringListFilter(filterLabels[FilterType.CustomerName], customerNames)),
  [FilterType.ServiceName]: getSimpleMultiSelectFilterConfig(FilterType.ServiceName, StringListFilter(filterLabels[FilterType.ServiceName], serviceNames))
});
function RevenueReportRow(props) {
  const items = compact([props.showPeriod && {
    value: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "revenue-breakdown-report-row-day",
      variant: "paragraph1",
      noWrap: true
    }, props.day)
  }, props.breakdowns.includes(Breakdown.Customer) && {
    value: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "revenue-breakdown-report-row-customer",
      variant: "paragraph1",
      noWrap: true,
      ellipsis: true
    }, props.customer || '')
  }, props.breakdowns.includes(Breakdown.Service) && {
    value: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "revenue-breakdown-report-row-service",
      variant: "paragraph1",
      noWrap: true,
      ellipsis: true
    }, props.service || '')
  }, {
    value: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "revenue-breakdown-report-row-revenue",
      variant: "paragraph1",
      noWrap: true
    }, props.revenue)
  }]);
  return /*#__PURE__*/React.createElement(TableList.Row, {
    items: items,
    rowId: props.index,
    "data-testid": "revenue-report-row-".concat(props.index),
    "data-heapid": "revenue-report-row"
  });
}
export function RevenueBreakdownReport() {
  const businessId = useStore(selectors.user.businessId);
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const [dataRangeOption, setDataRangeOption] = useState(DataRange.Last12Months);
  const [dateRange, setDateRange] = useState([calculateStartDate(formatShortUSDate(new Date()), DataRange.Last12Months), calculateEndDate(formatShortUSDate(new Date()), DataRange.Last12Months)]);
  const [aggregate, setAggregate] = useState(RevenueReportAggregateEnum.Month);
  const [breakdowns, setBreakdowns] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const filtersConfig = useMemo(() => makeFiltersConfig(availableServices, availableCustomers), [availableServices, availableCustomers]);
  const setters = {
    [FilterType.CustomerName]: setFilteredCustomers,
    [FilterType.ServiceName]: setFilteredServices
  };
  const values = {
    [FilterType.CustomerName]: filteredCustomers,
    [FilterType.ServiceName]: filteredServices
  };
  function addFilter(type) {
    setSelectedFilters(selectedFilters.concat(type));
  }
  function removeFilter(type) {
    setSelectedFilters(selectedFilters.filter(t => t !== type));
    switch (type) {
      case FilterType.CustomerName:
        setFilteredCustomers([]);
        break;
      case FilterType.ServiceName:
        setFilteredServices([]);
        break;
    }
  }
  useEffect(() => {
    if (businessId == null) return;
    api.reports.revenueReportServices({
      businessId
    }).then(services => {
      setAvailableServices(services);
    });
    api.reports.revenueReportCustomers({
      businessId
    }).then(customers => {
      setAvailableCustomers(customers);
    });
  }, [businessId]);
  useEffect(() => {
    if (businessId == null) return;
    setIsRefreshing(true);
    api.reports.revenueReport({
      businessId,
      start: dateRange[0],
      end: dateRange[1],
      breakdowns,
      aggregate,
      customers: filteredCustomers,
      services: filteredServices
    }).then(data => {
      setReportData(data);
      setIsRefreshing(false);
    });
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, [businessId, breakdowns.join(','), aggregate, ...dateRange, filteredServices, filteredCustomers]);
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "revenue-breakdown-report",
    header: "Revenue Breakdown Report",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      skin: "primary",
      "data-testid": "export-report-btn",
      onClick: () => exportCsv(makeCsv(revenueReportHeaderColumnDefs[breakdowns.sort().join(',')](aggregate).map(_ref7 => {
        let {
          value
        } = _ref7;
        return value;
      }), reportData), csvFileName)
    }, "Export"))
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: revenueReportColumnDefs[breakdowns.sort().join(',')](aggregate),
    numRows: reportData.length
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    flexWrap: true,
    "data-testid": "revenue-breakdown-report-filters"
  }, /*#__PURE__*/React.createElement(DataRangePicker, {
    dataRangeOption: dataRangeOption,
    startDate: dateRange[0],
    endDate: dateRange[1],
    onApply: (dataRange, startDate, endDate) => {
      setDataRangeOption(dataRange);
      setDateRange([startDate, endDate]);
    }
  }), /*#__PURE__*/React.createElement(AggregatePicker, {
    aggregate: aggregate,
    onApply: aggregate => {
      setAggregate(aggregate);
    }
  }), /*#__PURE__*/React.createElement(BreakdownPicker, {
    breakdowns: breakdowns,
    onApply: breakdowns => {
      setBreakdowns(breakdowns);
    }
  }), selectedFilters.map(type => renderFilterComponent(type, filtersConfig, selectedFilters, values[type], setters[type], removeFilter)), selectedFilters.length < Object.keys(FilterType).length && /*#__PURE__*/React.createElement(FilterSelector, {
    selected: selectedFilters,
    onSelect: addFilter
  }))), /*#__PURE__*/React.createElement(TableList.Header, {
    items: revenueReportHeaderColumnDefs[breakdowns.sort().join(',')](aggregate),
    "data-testid": "revenue-breakdown-report-table-header"
  })), isRefreshing ? /*#__PURE__*/React.createElement("div", {
    className: styles.loading,
    "data-testid": "agreements-table-loading"
  }, /*#__PURE__*/React.createElement(Spinner, null)) : reportData.length ? reportData.map((reportRow, index) => /*#__PURE__*/React.createElement(RevenueReportRow, {
    key: "".concat(reportRow.formattedDay, "-").concat(reportRow.customer, "-").concat(reportRow.service),
    index: index,
    day: reportRow.formattedDay,
    customer: reportRow.customer,
    service: reportRow.service,
    revenue: reportRow.revenue,
    breakdowns: breakdowns,
    showPeriod: aggregate !== RevenueReportAggregateEnum.AllTime
  })) : null));
}
function renderFilterComponent(type, filtersConfig, selectedFilters, value, onChange, onRemoveFilter) {
  return /*#__PURE__*/React.createElement(filtersConfig[type].component, {
    type,
    key: type,
    title: filterLabels[type],
    startOpen: selectedFilters.includes(type),
    value,
    onChange,
    onRemove: () => onRemoveFilter(type)
  });
}
const csvFileName = 'revenue-report.csv';
function makeCsv(headerRow, entries) {
  return [headerRow].concat(entries.map(entry => {
    return compact([entry.formattedDay, entry.customer, entry.service, entry.rawRevenue.toString()]);
  }));
}